import { MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationListItemResponse';
import { ProcessEventTriggerResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerResponse';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import { ProcessDataEffect, normalizeProcessDataEffects } from '@app/pages/admin/orchestration-data-records.util';
import { ProcessStorageEffect, normalizeProcessStorageEffects } from '@app/pages/admin/orchestration-object-storage.util';
import { parseAdminHash, serializeAdminHash } from '@app/pages/admin/admin-hash-state';
import { processKeysMatch } from '@app/pages/admin/orchestration-process-keys.util';

export interface ProcessDefinition {
  key: string;
  displayName?: string;
  description?: string;
  version: number;
  maxConcurrentAssignments?: number;
  requiredWorkerTags?: string[];
  dataEffects?: ProcessDataEffect[];
  storageEffects?: ProcessStorageEffect[];
  interruptionPolicy?: string;
  resumeAttemptLimit?: number;
  pauseAfterConsecutiveFailures?: number;
}

export type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

export type TriggerDirection = 'Incoming' | 'Outgoing' | 'Both directions';

export function findProcessDefinition(
  processes: ProcessDefinition[] | null | undefined,
  processKey: string,
): ProcessDefinition | null {
  const match = (processes ?? []).find(process => processKeysMatch(process.key, processKey));
  if (!match) {
    return null;
  }
  return {
    ...match,
    dataEffects: normalizeProcessDataEffects(match.dataEffects),
    storageEffects: normalizeProcessStorageEffects(
      (match as ProcessDefinition & { storageEffects?: unknown }).storageEffects,
    ),
  };
}

export function filterAssignmentsByProcessKey(
  items: MonitoredOperationListItemResponse[] | null | undefined,
  processKey: string,
): MonitoredOperationListItemResponse[] {
  return (items ?? []).filter(item => processKeysMatch(item.operationType, processKey));
}

export function filterSubscriptionsByProcessKey(
  items: ProcessSubscriptionResponse[] | null | undefined,
  processKey: string,
): ProcessSubscriptionResponse[] {
  return (items ?? []).filter(item => processKeysMatch(item.processDefinitionKey, processKey));
}

export function filterTriggersByProcessKey(
  items: ProcessEventTriggerResponse[] | null | undefined,
  processKey: string,
): ProcessEventTriggerResponse[] {
  return (items ?? []).filter(item =>
    processKeysMatch(item.sourceProcessDefinitionKey, processKey)
    || processKeysMatch(item.targetProcessDefinitionKey, processKey),
  );
}

export function triggerDirection(
  processKey: string,
  trigger: ProcessEventTriggerResponse,
): TriggerDirection {
  const isSource = processKeysMatch(trigger.sourceProcessDefinitionKey, processKey);
  const isTarget = processKeysMatch(trigger.targetProcessDefinitionKey, processKey);
  if (isSource && isTarget) {
    return 'Both directions';
  }
  if (isTarget) {
    return 'Incoming';
  }
  return 'Outgoing';
}

export function assignmentStateSeverity(state?: string | null): TagSeverity {
  switch (state) {
    case 'Completed':
    case 'Succeeded':
      return 'success';
    case 'Running':
      return 'info';
    case 'Cancelling':
      return 'warn';
    case 'Failed':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function isActiveAssignmentState(state?: string | null): boolean {
  return state === 'Pending' || state === 'Running' || state === 'Cancelling';
}

export function canCancelAssignment(state?: string | null): boolean {
  return state === 'Pending' || state === 'Running';
}

export function canRestartAssignment(state?: string | null): boolean {
  return state === 'Failed' || state === 'Cancelled' || state === 'Completed';
}

export const ASSIGNMENT_STATE_FILTERS = [
  'all',
  'Pending',
  'Running',
  'Cancelling',
  'Completed',
  'Failed',
  'Cancelled',
] as const;

export type AssignmentStateFilter = typeof ASSIGNMENT_STATE_FILTERS[number];

export interface AssignmentFilterRow {
  state: string;
  createdAt?: string;
}

export function filterAssignmentsByCreatedRange<T extends AssignmentFilterRow>(
  rows: T[],
  range?: Date[] | null,
): T[] {
  const bounds = createdRangeBounds(range);
  if (!bounds) {
    return rows;
  }
  return rows.filter(row => {
    if (!row.createdAt) {
      return false;
    }
    const created = Date.parse(row.createdAt);
    return !Number.isNaN(created) && created >= bounds.from && created <= bounds.to;
  });
}

export function filterAssignmentsByState<T extends AssignmentFilterRow>(
  rows: T[],
  stateFilter: AssignmentStateFilter,
): T[] {
  if (stateFilter === 'all') {
    return rows;
  }
  return rows.filter(row => row.state === stateFilter);
}

export function countAssignmentsByStateFilter<T extends AssignmentFilterRow>(
  rows: T[],
  stateFilter: AssignmentStateFilter,
): number {
  return filterAssignmentsByState(rows, stateFilter).length;
}

export function createdRangeApiBounds(range?: Date[] | null): { createdFrom?: string; createdTo?: string } {
  const bounds = createdRangeBounds(range);
  if (!bounds) {
    return {};
  }
  return {
    createdFrom: new Date(bounds.from).toISOString(),
    createdTo: new Date(bounds.to).toISOString(),
  };
}

function createdRangeBounds(range?: Date[] | null): { from: number; to: number } | null {
  const dates = (range ?? []).filter((value): value is Date => value instanceof Date);
  if (dates.length === 0) {
    return null;
  }
  const first = dates[0];
  const last = dates[1] ?? dates[0];
  return {
    from: startOfLocalDay(first).getTime(),
    to: endOfLocalDay(last).getTime(),
  };
}

/** Addresses, role names, and entity ids a notification rule sends to. */
export function notificationSubscriberLabel(
  recipients: ReadonlyArray<{ emailAddress?: string | null; roleName?: string | null; entityId?: string | null }>,
): string {
  return recipients
    .map(item => (item.emailAddress || item.roleName || item.entityId || '').trim())
    .filter(value => value.length > 0)
    .join(', ');
}

/** Rules with an empty source, or a source that is this process, including aliases. */
export function notificationRulesForProcess<T extends { sourceProcessDefinitionKey?: string | null }>(
  processKey: string,
  rules: readonly T[],
): T[] {
  return rules.filter(rule => {
    const source = rule.sourceProcessDefinitionKey?.trim() ?? '';
    return !source || processKeysMatch(source, processKey);
  });
}

export const PROCESS_PAGE_TABS = ['assignments', 'subscriptions', 'triggers', 'templates', 'subscribers', 'storage', 'access'] as const;

export type ProcessPageTab = (typeof PROCESS_PAGE_TABS)[number];

export function processPageTabFromHash(fragment: string | null | undefined): ProcessPageTab {
  const tab = parseAdminHash(fragment)['tab'];
  return PROCESS_PAGE_TABS.includes(tab as ProcessPageTab) ? tab as ProcessPageTab : 'assignments';
}

/** Assignments stay on the bare process URL. Other tabs use `#tab=`. */
export function processPageTabHash(tab: string | null | undefined): string | null {
  if (!tab || tab === 'assignments' || !PROCESS_PAGE_TABS.includes(tab as ProcessPageTab)) {
    return null;
  }
  return serializeAdminHash({ tab }) ?? null;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}
